import { states } from './StateUtil';

interface AddressAccount {
  addressLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export function createLocationFromAddress(account: AddressAccount): string {
  let lookupLocation = '';
  if (account.addressLine1) {
    lookupLocation = account.addressLine1 && account.addressLine1.trim() ? account.addressLine1 + ', ' : '';
  }
  if (account.city) {
    lookupLocation = lookupLocation + (account.city && account.city.trim() ? account.city + ', ' : '');
  }
  if (account.state) {
    lookupLocation = lookupLocation + (account.state && account.state.trim() ? account.state + ' ' : '');
  }
  if (account.zip) {
    lookupLocation = lookupLocation + (account.zip && account.zip.trim() ? account.zip : '');
  }
  if (lookupLocation && lookupLocation.substring(lookupLocation.length - 1, lookupLocation.length) === " ") {
    lookupLocation = lookupLocation.substring(0, lookupLocation.length - 1);
  }
  if (lookupLocation && lookupLocation.substring(lookupLocation.length - 2, lookupLocation.length) === ", ") {
    lookupLocation = lookupLocation.substring(0, lookupLocation.length - 2);
  }
  if (lookupLocation) {
    lookupLocation = lookupLocation + ', USA'
  }
  return lookupLocation;
}

const NAME_TYPES = Object.freeze({
  SHORT: 'short_name',
  LONG: 'long_name',
} as const);

type NameType = typeof NAME_TYPES.SHORT | typeof NAME_TYPES.LONG;

interface AddressComponent {
  long_name?: string;
  short_name?: string;
  types: string[];
}

interface GooglePlaceResult {
  address_components: AddressComponent[];
}

interface ComponentConfig {
  nameType: NameType;
  value: string;
}

interface AddressComponents {
  street_number: ComponentConfig;
  route: ComponentConfig;
  locality: ComponentConfig;
  administrative_area_level_3: ComponentConfig;
  administrative_area_level_2: ComponentConfig;
  administrative_area_level_1: ComponentConfig;
  postal_code: ComponentConfig;
}

interface AddressParts {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

export const getFullAddressPartsFromPlace = (googlePlaceResult: GooglePlaceResult): AddressParts => {

  //define the components we want to extract
  const components: AddressComponents = {
    street_number: {nameType: NAME_TYPES.SHORT, value: ''},
    route: {nameType: NAME_TYPES.LONG, value: ''},
    locality: {nameType: NAME_TYPES.LONG, value: ''},
    administrative_area_level_3: {nameType: NAME_TYPES.LONG, value: ''},
    administrative_area_level_2: {nameType: NAME_TYPES.LONG, value: ''},
    administrative_area_level_1: {nameType: NAME_TYPES.LONG, value: ''},
    postal_code: {nameType: NAME_TYPES.SHORT, value: ''}
  };

  //extract the component values from the Google place result
  for (const addressComponent of googlePlaceResult.address_components) {
    for (const type of addressComponent.types) {
      const component = components[type as keyof AddressComponents];
      if (component) {
        component.value = addressComponent[component.nameType] || '';
      }
    }
  }

  return {
    addressLine1: ((components.street_number.value + ' ' + components.route.value) || '').trim(),
    addressLine2: '',
    city: components.locality.value || components.administrative_area_level_3.value || components.administrative_area_level_2.value || '',
    state: components.administrative_area_level_1.value || '',
    zip: components.postal_code.value || '',
  };
};

/**
 * Common street-suffix/directional word abbreviations that Google's Geocoder
 * frequently normalizes to (e.g. "Parkway" -> "Pkwy", "Road" -> "Rd"). Mapped
 * to a single canonical short form so both the typed and suggested address
 * compare equal regardless of which form either one uses.
 */
const ADDRESS_WORD_ABBREVIATIONS: Record<string, string> = {
  street: 'st',
  avenue: 'ave',
  road: 'rd',
  boulevard: 'blvd',
  drive: 'dr',
  lane: 'ln',
  parkway: 'pkwy',
  court: 'ct',
  place: 'pl',
  highway: 'hwy',
  circle: 'cir',
  square: 'sq',
  terrace: 'ter',
  trail: 'trl',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  northeast: 'ne',
  northwest: 'nw',
  southeast: 'se',
  southwest: 'sw',
};

/**
 * Normalizes an address string for loose comparison: lowercases, strips
 * punctuation, collapses whitespace, canonicalizes common street-suffix and
 * directional abbreviations (Road/Rd, Parkway/Pkwy, North/N, etc), and drops
 * a trailing "USA"/"United States" country suffix. Google's Geocoder almost
 * always appends the country to `formatted_address`, but most manually typed
 * addresses omit it — this normalization keeps that difference from forcing
 * an unnecessary confirmation step.
 */
const normalizeAddressForComparison = (address: string): string => {
  const words = address
    .toLowerCase()
    .replace(/[.,#]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => ADDRESS_WORD_ABBREVIATIONS[word] || word);

  if (words.length && words[words.length - 1] === 'usa') {
    words.pop();
  } else if (words.length >= 2 && words[words.length - 2] === 'united' && words[words.length - 1] === 'states') {
    words.pop();
    words.pop();
  }

  return words.join(' ');
};

/**
 * Returns true when `typedAddress` and `suggestedAddress` are effectively the
 * same address once normalized (case/punctuation/whitespace-insensitive).
 * Used to decide whether a manually typed address is close enough to Google's
 * top geocoded suggestion to skip the address-confirmation modal.
 */
export const isAddressMatch = (typedAddress: string, suggestedAddress: string): boolean => {
  if (!typedAddress || !suggestedAddress) {
    return false;
  }
  return normalizeAddressForComparison(typedAddress) === normalizeAddressForComparison(suggestedAddress);
};

/**
 * Extracts the street-level portion of a typed address: everything before
 * the first comma, or the whole string if there's no comma.
 * @returns The typed street text, e.g. "1 Long Beach St".
 */
export const extractTypedAddressLine1 = (typedAddress: string): string => {
  const [firstSegment] = typedAddress.split(',');
  return (firstSegment || typedAddress).trim();
};

/**
 * Extracts a US ZIP code the supplier typed at the end of their address,
 * ignoring a trailing "USA"/"United States". Anchored to the end so a
 * five-digit street number ("12345 Main St, Springfield, IL") is never
 * mistaken for a ZIP.
 * @returns The ZIP as typed, or `''` when the text ends with none.
 * @example extractTypedZip('1 Main St, Springfield, IL 62701') // '62701'
 * @example extractTypedZip('12345 Main St, Springfield, IL') // ''
 */
export const extractTypedZip = (typedAddress: string): string => {
  const match = typedAddress.match(/\b(\d{5}(?:-\d{4})?)\b(?:\s*,?\s*(?:usa|united states))?\s*$/i);
  return match ? match[1] : '';
};

/**
 * Uppercases the first letter of each word, leaving the rest of each word
 * untouched (does not lowercase, so abbreviations like "CA"/"USA" stay intact).
 * @returns The capitalized text.
 * @example capitalizeWords('1 long beach st, ca') // '1 Long Beach St, Ca'
 */
export const capitalizeWords = (text: string): string => text.replace(/\b\w/g, (char) => char.toUpperCase());


/**
 * A word-like token from tokenized text. `normalized` (lowercase) is used
 * for comparison; `original` preserves verbatim casing for extraction;
 * `start` is the character offset in the source string, for slicing.
 */
interface TypedToken {
  normalized: string;
  original: string;
  start: number;
}

/** Splits free text into `TypedToken`s, treating light punctuation as delimiters. */
const tokenize = (text: string): TypedToken[] => {
  const tokens: TypedToken[] = [];
  const regex = /[^\s,.#]+/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    tokens.push({ normalized: match[0].toLowerCase(), original: match[0], start: match.index });
  }
  return tokens;
};

/** Same tokenization, but only the normalized words (for phrases to search for). */
const normalizeWords = (text: string): string[] => tokenize(text).map((token) => token.normalized);

/** A single located run of `phraseWords` within a token array, with its position. */
interface PhraseOccurrence {
  tokens: TypedToken[];
  startIndex: number;
  endIndex: number;
}

/**
 * Finds every contiguous, word-boundary-aligned occurrence of `phraseWords`
 * within `tokens` (whole-word match, left to right).
 * @returns All matching occurrences, each with its tokens and token-index range.
 */
const findAllPhraseOccurrences = (tokens: TypedToken[], phraseWords: string[]): PhraseOccurrence[] => {
  if (!phraseWords.length) return [];
  const occurrences: PhraseOccurrence[] = [];
  for (let i = 0; i <= tokens.length - phraseWords.length; i += 1) {
    if (phraseWords.every((word, j) => tokens[i + j].normalized === word)) {
      occurrences.push({
        tokens: tokens.slice(i, i + phraseWords.length),
        startIndex: i,
        endIndex: i + phraseWords.length - 1,
      });
    }
  }
  return occurrences;
};

/**
 * Resolves a state's full name and abbreviation from either form.
 * @returns `{ name, abbreviation }`, or `undefined` if not a recognized state.
 */
const resolveStateNameAndAbbreviation = (
  state: string,
): { name: string; abbreviation: string } | undefined => {
  const trimmed = state.trim();
  const abbreviationByName = states.get(trimmed);
  if (abbreviationByName) {
    return { name: trimmed, abbreviation: abbreviationByName };
  }
  const upper = trimmed.toUpperCase();
  const nameByAbbreviation = [...states.entries()].find(([, abbr]) => abbr === upper);
  return nameByAbbreviation ? { name: nameByAbbreviation[0], abbreviation: nameByAbbreviation[1] } : undefined;
};

/**
 * Checks whether a single comma-delimited segment is, on its own, a
 * recognizable US state (full name or abbreviation, case-insensitive,
 * tolerating a trailing zip code).
 * @returns `true` if the segment is a state.
 * @example segmentAssertsState('IL 62701') // true
 */
const segmentAssertsState = (segment: string): boolean => {
  const withoutZip = segment.replace(/\d[\d-]*$/, '').trim();
  if (!withoutZip) {
    return false;
  }
  const lower = withoutZip.toLowerCase();
  for (const [name, abbreviation] of states.entries()) {
    if (lower === name.toLowerCase() || lower === abbreviation.toLowerCase()) {
      return true;
    }
  }
  return false;
};

/** The street/city/state a supplier explicitly asserted via comma structure. */
interface AssertedAddressParts {
  addressLine1: string;
  city: string;
  state: string;
}

/**
 * Parses the street, city, and state a supplier explicitly asserted via a
 * "Street, City, ST" comma structure, reading the typed text alone — never
 * Google's geocode. Requires three comma-delimited segments; a two-segment
 * "street, state" (no distinct city) does not count. A trailing
 * "USA"/"United States" segment is ignored, and a zip trailing the state
 * segment is stripped.
 * @returns The asserted parts, or `undefined` when the text doesn't assert
 * all three.
 * @example parseAssertedAddressParts('1 Main St, Springfield, IL 62701')
 * // { addressLine1: '1 Main St', city: 'Springfield', state: 'IL' }
 */
const parseAssertedAddressParts = (typedAddress: string): AssertedAddressParts | undefined => {
  const segments = typedAddress
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (!segments.length) {
    return undefined;
  }

  // A trailing "USA"/"United States" segment isn't the state itself — read
  // the segment before it instead (e.g. "123 Main St, Springfield, IL, USA").
  const lastIsCountry = /^(usa|united states)$/i.test(segments[segments.length - 1]);
  const meaningfulSegments = lastIsCountry ? segments.slice(0, -1) : segments;

  // Require a street segment ahead of the city/state pair, so a bare
  // "City, ST" (with no street at all) doesn't qualify — this is only ever
  // consulted after a street-level minimum has already been required, so a
  // true assertion must include all three.
  if (meaningfulSegments.length < 3) {
    return undefined;
  }

  const stateSegment = meaningfulSegments[meaningfulSegments.length - 1];
  const city = meaningfulSegments[meaningfulSegments.length - 2];

  if (!city || !segmentAssertsState(stateSegment)) {
    return undefined;
  }

  return {
    addressLine1: meaningfulSegments.slice(0, -2).join(', '),
    city,
    state: stateSegment.replace(/\d[\d-]*$/, '').trim(),
  };
};

/**
 * Checks whether typed address text explicitly asserts its own street,
 * city, AND state via a "Street, City, ST" comma structure — regardless of
 * whether the city/state match Google's geocode.
 * @returns `true` if the text asserts a distinct street, city, and state.
 * @example typedAddressAssertsCityState('1 Main St, Springfield, IL') // true
 * @example typedAddressAssertsCityState('1 Main St, CA') // false (no city)
 */
export const typedAddressAssertsCityState = (typedAddress: string): boolean =>
  Boolean(parseAssertedAddressParts(typedAddress));

/**
 * Locates the city and state tokens within typed address text, using a
 * geocoded city/state as the search key. Prefers a city occurrence
 * immediately followed by a state occurrence — disambiguating a city name
 * that also appears in the street (e.g. "123 Portland St, Portland, OR") —
 * falling back to the first state occurrence after the city. A state word
 * appearing only before the city (e.g. "California" in "California St",
 * ahead of the city "San Francisco") doesn't count as a genuine assertion.
 * @returns The located `cityTokens`/`stateTokens`, or `undefined` if either
 * isn't found in the typed text (in the required order).
 */
const locateCityStateTokens = (
  typedAddress: string,
  city: string,
  state: string,
): { cityTokens: TypedToken[]; stateTokens: TypedToken[] } | undefined => {
  if (!typedAddress || !typedAddress.trim() || !city || !state) {
    return undefined;
  }

  const tokens = tokenize(typedAddress);

  const cityOccurrences = findAllPhraseOccurrences(tokens, normalizeWords(city));
  if (!cityOccurrences.length) {
    return undefined;
  }

  const resolvedState = resolveStateNameAndAbbreviation(state);
  const stateCandidates = resolvedState
    ? [normalizeWords(resolvedState.name), normalizeWords(resolvedState.abbreviation)]
    : [normalizeWords(state)];

  const stateOccurrences = stateCandidates.flatMap((candidate) => findAllPhraseOccurrences(tokens, candidate));
  if (!stateOccurrences.length) {
    return undefined;
  }

  for (const cityOccurrence of cityOccurrences) {
    const adjacentState = stateOccurrences.find(
      (stateOccurrence) => stateOccurrence.startIndex === cityOccurrence.endIndex + 1,
    );
    if (adjacentState) {
      return { cityTokens: cityOccurrence.tokens, stateTokens: adjacentState.tokens };
    }
  }

  // No exact "...City ST" adjacency — fall back to the first city occurrence
  // and the first state occurrence that comes after it. A state word found
  // only *before* the city (e.g. "California" as part of a street name like
  // "California St", ahead of the city "San Francisco") isn't a genuine
  // state assertion, so it must not count here.
  const cityOccurrence = cityOccurrences[0];
  const stateAfterCity = stateOccurrences.find((occurrence) => occurrence.startIndex > cityOccurrence.endIndex);
  if (!stateAfterCity) {
    return undefined;
  }

  return { cityTokens: cityOccurrence.tokens, stateTokens: stateAfterCity.tokens };
};

/**
 * Checks whether typed address text contains the given city and state
 * verbatim (state matched by full name or abbreviation).
 * @returns `true` if both are found in the typed text.
 */
export const typedAddressIncludesCityState = (
  typedAddress: string,
  city: string,
  state: string,
): boolean => Boolean(locateCityStateTokens(typedAddress, city, state));

/**
 * Cuts the street portion off right before wherever the city was located in
 * the typed text, rather than assuming a comma always separates them — a
 * supplier can type a run-on address with no punctuation at all (e.g.
 * "1 Long Beach St Anytown CA"), and a naive comma-split would otherwise
 * fold the city/state into addressLine1.
 * @returns The street portion, falling back to the first comma-separated
 * segment when the cut would leave nothing.
 */
const sliceAddressLine1BeforeCity = (typedAddress: string, cityTokens: TypedToken[]): string => {
  const addressLine1 = typedAddress.slice(0, cityTokens[0].start).replace(/,\s*$/, '').trim();
  return addressLine1 || extractTypedAddressLine1(typedAddress);
};

/**
 * Locates the city on its own, for typed text that names a city but asserts
 * no state after it (e.g. "1 California St San Francisco"). Only accepts a
 * trailing city, since a city name elsewhere in the text is more likely part
 * of the street (e.g. "123 Portland St" for the city Portland).
 * @returns The city's tokens, or `undefined` when no trailing city is found.
 */
const locateTrailingCityTokens = (typedAddress: string, city: string): TypedToken[] | undefined => {
  if (!typedAddress || !typedAddress.trim() || !city) {
    return undefined;
  }

  const tokens = tokenize(typedAddress);
  const occurrences = findAllPhraseOccurrences(tokens, normalizeWords(city));
  const trailing = occurrences.find((occurrence) => occurrence.endIndex === tokens.length - 1);

  return trailing?.tokens;
};

/**
 * Extracts the street/city/state as typed, preserving the supplier's own
 * spelling/casing/abbreviation, using the geocoded city/state as a search
 * key to locate them in the typed text.
 * @returns `addressLine1`, `city`, `state`, and `matched` — `matched` is
 * `false` when the city/state couldn't be located together in the typed
 * text, in which case `city`/`state` fall back to the geocoded values.
 */
export const extractTypedAddressParts = (
  typedAddress: string,
  geocoded: GeocodedAddress,
): { addressLine1: string; city: string; state: string; matched: boolean } => {
  // An explicit "Street, City, ST" assertion is authoritative and is read
  // without consulting Google at all. Google's geocode can legitimately
  // disagree with a valid assertion (e.g. "1 Main St, Springfield, IL"
  // geocoded to Springfield, MO) — that disagreement is precisely why the
  // confirmation dialog opened, so resolving it against Google here would
  // silently discard the very values the supplier chose to keep.
  const asserted = parseAssertedAddressParts(typedAddress);
  if (asserted) {
    return { ...asserted, matched: true };
  }

  const located = locateCityStateTokens(typedAddress, geocoded.city, geocoded.state);

  if (!located) {
    // No state was asserted, but the supplier may still have typed the city
    // (e.g. "1 California St San Francisco"). Trim it off the street line
    // anyway, otherwise the city is duplicated into both addressLine1 and
    // city. The state still comes from Google, since none was asserted.
    const trailingCityTokens = locateTrailingCityTokens(typedAddress, geocoded.city);

    return {
      addressLine1: trailingCityTokens
        ? sliceAddressLine1BeforeCity(typedAddress, trailingCityTokens)
        : extractTypedAddressLine1(typedAddress),
      city: trailingCityTokens ? trailingCityTokens.map((token) => token.original).join(' ') : geocoded.city,
      state: geocoded.state,
      matched: false,
    };
  }

  return {
    addressLine1: sliceAddressLine1BeforeCity(typedAddress, located.cityTokens),
    city: located.cityTokens.map((token) => token.original).join(' '),
    state: located.stateTokens.map((token) => token.original).join(' '),
    matched: true,
  };
};

/** An address part that Google's geocode would supply, with its exact value. */
export interface GoogleSuppliedAddressPart {
  /** Display label, e.g. `'City'`. */
  label: string;
  /** The exact value that would be saved, e.g. `'San Francisco'`. */
  value: string;
}

/**
 * Determines which address parts would be filled in from Google's geocode
 * rather than from the supplier's own text, if they keep their entry.
 * Intentionally errs toward over-reporting, since the purpose is to warn.
 * @returns The supplied parts with their exact values — empty when the
 * typed text already supplies every part.
 * @example getGoogleSuppliedAddressParts('1 California St San Francisco', geocoded)
 * // [{ label: 'State', value: 'California' }, { label: 'ZIP code', value: '94104' }]
 */
export const getGoogleSuppliedAddressParts = (
  typedAddress: string,
  geocoded: Pick<GeocodedAddress, 'city' | 'state' | 'zip'>,
): GoogleSuppliedAddressPart[] => {
  const parts: GoogleSuppliedAddressPart[] = [];
  const tokens = tokenize(typedAddress);

  // Mirror `extractTypedAddressParts`: when the supplier explicitly asserts
  // a city and state, both are kept as typed even if Google disagrees, so
  // neither is Google-supplied and warning about them would be misleading.
  const asserted = parseAssertedAddressParts(typedAddress);

  if (!asserted) {
    if (geocoded.city && !findAllPhraseOccurrences(tokens, normalizeWords(geocoded.city)).length) {
      parts.push({ label: 'City', value: geocoded.city });
    }

  // A state only counts as the supplier's own when it's asserted in the
  // conventional position (after the city) — a state word appearing only
  // earlier, as part of a street name (e.g. "California St"), isn't an
  // assertion. `locateCityStateTokens` already encodes that rule.
    if (geocoded.state && !locateCityStateTokens(typedAddress, geocoded.city, geocoded.state)) {
      parts.push({ label: 'State', value: geocoded.state });
    }
  }

  if (geocoded.zip && !typedAddress.includes(geocoded.zip)) {
    parts.push({ label: 'ZIP code', value: geocoded.zip });
  }

  return parts;
};

/**
 * Checks whether the supplier's own typed text begins with a street number
 * followed by a street name — the detail Google omits when it can only match
 * an address at zip/locality level.
 * @returns `true` if the street portion looks like "4471 Phantom Yard Road".
 * @example typedAddressAssertsStreet('4471 Phantom Yard Rd, Pooler, GA') // true
 * @example typedAddressAssertsStreet('Pooler, GA 31322') // false
 */
export const typedAddressAssertsStreet = (typedAddress: string): boolean =>
  /^\s*\d[\w-]*\s+\S+/.test(extractTypedAddressLine1(typedAddress));

export interface GeocodedAddress {
  formattedAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  addressLatitude: number;
  addressLongitude: number;
}

export type AddressConfirmationCheckResult =
  // Geocoded to essentially the same address the user typed — safe to accept
  // without prompting.
  | { status: 'match'; address: GeocodedAddress }
  // Geocoded to a different address than what was typed — caller should show
  // AddressConfirmationDialog so the user can pick one.
  | { status: 'needs-confirmation'; address: GeocodedAddress }
  // Google recognized the city/state/zip but not the street, while the
  // supplier did type a street number and name. This is the "real address
  // Google hasn't mapped yet" case: there's no alternative to offer, so the
  // caller should ask the user to confirm their own entry, which is saved
  // with Google's approximate (zip/locality centroid) coordinates.
  | { status: 'unverified-street'; address: GeocodedAddress }
  // Google could not geocode the string at all.
  | { status: 'not-found' }
  // Geocoded, but missing a street number/route (e.g. "Los Angeles, FL").
  | { status: 'incomplete' }
  // Only surfaced for the 'street' minimum level: the address meets the
  // street-number/route minimum and geocoded successfully, but the
  // supplier's own typed text doesn't actually contain a city or state —
  // Google filled in its own guess for both, which isn't safe to trust
  // silently (e.g. "1 Long Beach St" alone).
  | { status: 'missing-city-state' };

interface GeocoderLatLng {
  lat: () => number;
  lng: () => number;
}

interface GeocoderResult {
  formatted_address: string;
  address_components: AddressComponent[];
  geometry: { location: GeocoderLatLng; location_type?: string };
  /**
   * Google flags a result as a partial match when it had to loosen/guess part
   * of the query to find any result at all (e.g. an unverifiable house
   * number on an otherwise-real street) — a signal that the geocode is a
   * best-effort guess rather than a confirmed, precise address.
   */
  partial_match?: boolean;
}

/**
 * Checks whether a geocoder result is a low-confidence guess: Google
 * loosened the query (`partial_match`) or the pin isn't an exact
 * `'ROOFTOP'` match.
 * @returns `true` if the result shouldn't be auto-accepted without confirmation.
 */
export const isLowConfidenceGeocode = (result: GeocoderResult): boolean =>
  Boolean(result.partial_match) || result.geometry.location_type !== 'ROOFTOP';

/**
 * The minimum level of address detail required to proceed past the
 * `'incomplete'` check:
 * - `'street'` (default) — requires a street number + route (e.g. the
 *   Create/Edit Location wizard, where a precise pin location is required).
 * - `'cityState'` — requires a city + state, with the street number/route
 *   optional (e.g. Company Details, where the account API rejects updates
 *   that are missing either a city or a state).
 */
export type AddressMinimumLevel = 'street' | 'cityState';

/**
 * Geocodes a manually typed address (i.e. not selected from the Google Places
 * autocomplete dropdown) via `google.maps.Geocoder` and determines how Save
 * should proceed. Used to power the address-confirmation flow in both the
 * Create/Edit Location wizard and the Company Details form: manual address
 * entry should not silently fail or hard-block Save, but a mismatch between
 * what the user typed and Google's top match should be confirmed with the
 * user rather than saved unverified.
 *
 * @param typedAddress The address string the user typed into the input field.
 * @param minimumLevel Governs how strict the `'incomplete'` check is — see
 * `AddressMinimumLevel`. Defaults to `'street'`.
 */
export const checkAddressForConfirmation = (
  typedAddress: string,
  minimumLevel: AddressMinimumLevel = 'street',
): Promise<AddressConfirmationCheckResult> => {
  return new Promise((resolve) => {
    const google = window.google;
    if (!typedAddress || !typedAddress.trim() || !google?.maps?.Geocoder) {
      resolve({ status: 'not-found' });
      return;
    }

    const geocoder = new google.maps.Geocoder();
    void geocoder.geocode(
      { address: typedAddress, componentRestrictions: { country: 'US' } },
      (results: GeocoderResult[] | null, status: string) => {
        if (status !== 'OK' || !results || !results.length) {
          resolve({ status: 'not-found' });
          return;
        }

        const topResult = results[0];
        const parts = getFullAddressPartsFromPlace(topResult);
        const hasCityState = Boolean(parts.city && parts.state);
        // 'cityState' requires city AND state — a street number alone does not
        // satisfy it, because the account API rejects updates missing either.
        const meetsMinimum = minimumLevel === 'cityState' ? hasCityState : Boolean(parts.addressLine1);

        if (!meetsMinimum) {
          // Google sometimes falls back to a broad country/region-level match
          // (e.g. componentRestrictions causing "United States" to match
          // otherwise-unrecognizable input) instead of returning zero results.
          // When there's no locality/state/zip at all, treat it the same as
          // not-found rather than surfacing a misleading "incomplete" message.
          if (!parts.city && !parts.state && !parts.zip) {
            resolve({ status: 'not-found' });
            return;
          }
          // Google recognized the city/state but not the street, yet the
          // supplier typed a street number and name plus a corroborating
          // city/state. That's a real-but-unmapped address (new construction,
          // private yard, recently renamed road) rather than bad input, so
          // let them confirm their own entry instead of hard-blocking. The
          // geocode's zip/locality centroid supplies approximate coordinates.
          if (
            minimumLevel === 'street' &&
            parts.city &&
            parts.state &&
            typedAddressAssertsStreet(typedAddress) &&
            typedAddressAssertsCityState(typedAddress)
          ) {
            resolve({
              status: 'unverified-street',
              address: {
                formattedAddress: topResult.formatted_address,
                ...parts,
                addressLatitude: topResult.geometry.location.lat(),
                addressLongitude: topResult.geometry.location.lng(),
              },
            });
            return;
          }
          resolve({ status: 'incomplete' });
          return;
        }

        const address: GeocodedAddress = {
          formattedAddress: topResult.formatted_address,
          ...parts,
          addressLatitude: topResult.geometry.location.lat(),
          addressLongitude: topResult.geometry.location.lng(),
        };

        // Only for the 'street' minimum level (Create/Edit Location wizard):
        // require the supplier's own typed text to actually contain a city
        // and state, not just Google's geocode of it. Google will supply its
        // own best guess for both even when the supplier only typed a bare
        // street — that guess can be wrong/ambiguous (e.g. "1 Long Beach St"
        // exists, ambiguously, in multiple states), so it isn't safe to
        // silently trust. The 'cityState' minimum level (Company Details)
        // already requires city+state to be present in the geocoded result
        // via `meetsMinimum` above, so it's unaffected by this check.
        if (
          minimumLevel === 'street' &&
          !typedAddressIncludesCityState(typedAddress, address.city, address.state)
        ) {
          // The supplier may still have typed a city/state of their own that
          // simply conflicts with Google's top guess (e.g. a same-named city
          // that exists in more than one state) rather than having typed no
          // city/state at all. In that case they *did* provide the required
          // information — let the confirmation dialog offer "Your entry"
          // instead of blocking outright.
          if (typedAddressAssertsCityState(typedAddress)) {
            resolve({ status: 'needs-confirmation', address });
            return;
          }
          resolve({ status: 'missing-city-state' });
          return;
        }

        // For the 'street' level, also require a confident geocode (not just
        // a textual match) before auto-accepting — Google can return a
        // `formatted_address` that still echoes the typed house number even
        // when it's only an interpolated guess along the street (see
        // `isLowConfidenceGeocode`). The 'cityState' level's match decision
        // is left as it was (Company Details doesn't need street-level pin
        // precision, so this signal doesn't apply there).
        const confidentMatch =
          isAddressMatch(typedAddress, address.formattedAddress) &&
          (minimumLevel !== 'street' || !isLowConfidenceGeocode(topResult));

        if (confidentMatch) {
          // The formatted-address text matches what the supplier typed, but
          // Google's parsed street-number component can still be missing on
          // an approximate/interpolated result. Prefer the supplier's own
          // typed street text over the (potentially incomplete) parsed one —
          // only for the 'street' minimum level (Create/Edit Location
          // wizard), and only when a street portion was actually parsed.
          // The 'cityState' level (Company Details) doesn't need
          // street-level pin precision and shares this code path with
          // inputs that aren't always a clean "street, city, state" — e.g.
          // a run-on "123 Main St Dallas TX 75201" — so it keeps using
          // Google's parsed `addressLine1` untouched rather than risking
          // city/state/zip getting folded into it.
          if (minimumLevel === 'street' && address.addressLine1) {
            address.addressLine1 = extractTypedAddressLine1(typedAddress) || address.addressLine1;
          }
          resolve({ status: 'match', address });
          return;
        }

        resolve({ status: 'needs-confirmation', address });
      }
    );
  });
};
